import { supabase } from '../lib/supabase';

// OneSignal REST API configuration
// Get these from your OneSignal Dashboard: https://app.onesignal.com
// Go to Settings -> Keys & IDs
const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID || '';
const ONESIGNAL_REST_API_KEY = import.meta.env.VITE_ONESIGNAL_REST_API_KEY || '';

interface PushNotificationPayload {
  recipientUserId: string;
  title: string;
  message: string;
  data?: Record<string, any>;
}

/**
 * OneSignal Push Notification Service
 * Sends push notifications to mobile app users
 */
export const oneSignalService = {
  /**
   * Send a push notification to a specific user
   */
  async sendPushNotification(payload: PushNotificationPayload): Promise<boolean> {
    try {
      // Get the user's OneSignal player ID from database
      const { data: userProfile, error: profileError } = await supabase
        .from('user_profiles')
        .select('onesignal_player_id, push_notifications_enabled')
        .eq('id', payload.recipientUserId)
        .single();

      if (profileError || !userProfile?.onesignal_player_id) {
        console.log('No OneSignal player ID found for user:', payload.recipientUserId);
        return false;
      }

      if (!userProfile.push_notifications_enabled) {
        console.log('Push notifications disabled for user:', payload.recipientUserId);
        return false;
      }

      // Check if OneSignal is configured
      if (!ONESIGNAL_APP_ID || !ONESIGNAL_REST_API_KEY) {
        console.log('OneSignal not configured, queueing notification instead');
        // Queue the notification in database for edge function to process
        await this.queueNotification(payload, userProfile.onesignal_player_id);
        return true;
      }

      // Determine if using v2 API key (starts with os_v2_)
      const isV2Key = ONESIGNAL_REST_API_KEY.startsWith('os_v2_');
      
      // Send directly via OneSignal REST API
      const response = await fetch('https://onesignal.com/api/v1/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': isV2Key ? `Key ${ONESIGNAL_REST_API_KEY}` : `Basic ${ONESIGNAL_REST_API_KEY}`,
        },
        body: JSON.stringify({
          app_id: ONESIGNAL_APP_ID,
          include_subscription_ids: [userProfile.onesignal_player_id],
          contents: { en: payload.message },
          headings: { en: payload.title },
          data: {
            ...payload.data,
            type: 'new_message',
          },
          target_channel: 'push',
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('OneSignal API error:', result);
        return false;
      }

      console.log('Push notification sent successfully:', result.id);
      return true;
    } catch (error) {
      console.error('Error sending push notification:', error);
      return false;
    }
  },

  /**
   * Queue notification for edge function processing
   */
  async queueNotification(payload: PushNotificationPayload, playerId: string): Promise<void> {
    try {
      await supabase.from('push_notification_queue').insert({
        recipient_user_id: payload.recipientUserId,
        title: payload.title,
        message: payload.message,
        data: {
          ...payload.data,
          player_id: playerId,
        },
        status: 'pending',
      });
      console.log('Notification queued for processing');
    } catch (error) {
      console.error('Error queueing notification:', error);
    }
  },

  /**
   * Send notification for new message
   */
  async notifyNewMessage(
    recipientUserId: string,
    senderName: string,
    messageContent: string,
    conversationId?: string,
    campaignId?: string
  ): Promise<boolean> {
    const previewContent = messageContent.length > 100 
      ? messageContent.substring(0, 100) + '...' 
      : messageContent;

    return this.sendPushNotification({
      recipientUserId,
      title: `New Message from ${senderName}`,
      message: previewContent,
      data: {
        conversation_id: conversationId,
        campaign_id: campaignId,
      },
    });
  },

  /**
   * Send notification for campaign broadcast
   */
  async notifyBroadcast(
    recipientUserId: string,
    campaignName: string,
    messageContent: string,
    campaignId: string
  ): Promise<boolean> {
    const previewContent = messageContent.length > 100 
      ? messageContent.substring(0, 100) + '...' 
      : messageContent;

    return this.sendPushNotification({
      recipientUserId,
      title: `📢 Broadcast from ${campaignName}`,
      message: previewContent,
      data: {
        campaign_id: campaignId,
        is_broadcast: true,
      },
    });
  },
};
